import React, { useState } from 'react';
import { 
  UserPlus, 
  Search, 
  Calendar, 
  Plane, 
  MapPin, 
  FileText, 
  CheckCircle, 
  ArrowRight,
  Info 
} from 'lucide-react';
import Navbar from '../components/accueil/Navbar';
import PageBanner from './Banner';
import Footer from '../components/accueil/Footer';
import ChatWidget from '../components/accueil/ChatWidget';
import useGlobale from '../components/global';

const TutorielScreen = () => {
  const { authenticated, user, logout } = useGlobale();
  const [activeTab, setActiveTab] = useState('commencer');

  const tutorialSections = {
    commencer: {
      title: "Pour Commencer",
      steps: [
        {
          icon: <UserPlus className="w-8 h-8 text-brand-600" />,
          title: "Créez votre compte",
          description: "Inscrivez-vous en fournissant vos informations personnelles. Vous recevrez un e-mail de confirmation pour activer votre compte."
        },
        {
          icon: <FileText className="w-8 h-8 text-brand-600" />,
          title: "Complétez votre profil",
          description: "Ajoutez vos informations de voyage et vos préférences pour une meilleure expérience personnalisée."
        },
        {
          icon: <CheckCircle className="w-8 h-8 text-brand-600" />,
          title: "Validez votre identité",
          description: "Pour votre sécurité, nous vérifions l'identité de tous nos utilisateurs."
        }
      ]
    },
    recherche: {
      title: "Rechercher un Voyage",
      steps: [
        {
          icon: <Search className="w-8 h-8 text-brand-600" />,
          title: "Utilisez les filtres",
          description: "Affinez votre recherche par date, destination, type de transport, etc."
        },
        {
          icon: <Calendar className="w-8 h-8 text-brand-600" />,
          title: "Sélectionnez vos dates",
          description: "Choisissez vos dates de voyage de manière flexible avec notre calendrier interactif."
        },
        {
          icon: <MapPin className="w-8 h-8 text-brand-600" />,
          title: "Explorez les destinations",
          description: "Découvrez les différentes destinations disponibles au Niger et dans la région."
        }
      ]
    },
    reservation: {
      title: "Réserver un Voyage",
      steps: [
        {
          icon: <Plane className="w-8 h-8 text-brand-600" />,
          title: "Choisissez votre itinéraire",
          description: "Sélectionnez vos vols ou transports en fonction de vos préférences."
        },
        {
          icon: <FileText className="w-8 h-8 text-brand-600" />,
          title: "Remplissez les informations",
          description: "Fournissez les détails nécessaires pour tous les voyageurs."
        },
        {
          icon: <CheckCircle className="w-8 h-8 text-brand-600" />,
          title: "Confirmez la réservation",
          description: "Vérifiez les détails et procédez au paiement sécurisé."
        }
      ]
    }
  };

  return (
    <>
      <Navbar authenticated={authenticated} user={user} logout={logout} />
      <PageBanner currentPage="Tutoriel" />

      <div className="bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          {/* En-tête du tutoriel */}
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comment utiliser Niger Voyage ?
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Suivez ce guide étape par étape pour découvrir toutes les fonctionnalités 
              de notre plateforme et planifier vos voyages en toute simplicité.
            </p>
          </div>

          {/* Navigation des sections */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {Object.entries(tutorialSections).map(([key, section]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`px-6 py-3 rounded-lg font-medium transition-all
                          ${activeTab === key 
                            ? 'bg-brand-600 text-white shadow-lg' 
                            : 'bg-white text-gray-700 hover:bg-brand-50'}`}
              >
                {section.title}
              </button>
            ))}
          </div>

          {/* Contenu du tutoriel */}
          <div className="max-w-4xl mx-auto">
            {/* Étapes */}
            <div className="space-y-8">
              {tutorialSections[activeTab].steps.map((step, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow"
                >
                  <div className="flex items-start gap-6">
                    <div className="bg-brand-50 p-4 rounded-lg">
                      {step.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {step.title}
                      </h3>
                      <p className="text-gray-600">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Conseils supplémentaires */}
            <div className="mt-12 bg-blue-50 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <Info className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">
                    Conseils utiles
                  </h3>
                  <ul className="space-y-2 text-blue-800">
                    <li className="flex items-center gap-2">
                      <ArrowRight className="w-4 h-4" />
                      Gardez vos documents de voyage à portée de main lors de la réservation
                    </li>
                    <li className="flex items-center gap-2">
                      <ArrowRight className="w-4 h-4" />
                      Vérifiez les conditions d'entrée de votre destination
                    </li>
                    <li className="flex items-center gap-2">
                      <ArrowRight className="w-4 h-4" />
                      Activez les notifications pour suivre vos réservations
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Bouton d'assistance */}
            <div className="text-center mt-12">
              <p className="text-gray-600 mb-4">
                Vous avez encore des questions ?
              </p>
              <button className="inline-flex items-center gap-2 px-6 py-3 bg-brand-600 text-white 
                               rounded-lg hover:bg-brand-700 transition-colors">
                Contactez notre support
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <ChatWidget />
    </>
  );
};

export default TutorielScreen;
